/**
 * 特定场景的,非通用
 */

/** 普通中文数字（数量 / 非金额） */
const CN_NUM_NORMAL = [
    '零', '一', '二', '三', '四',
    '五', '六', '七', '八', '九',
] as const

/** 金额大写数字 */
const CN_NUM_MONEY = [
    '零', '壹', '贰', '叁', '肆',
    '伍', '陆', '柒', '捌', '玖',
] as const

/**
 * 将数字转换为中文显示
 * 支持到千亿级别
 * @param num - 要转换的数字
 * @returns 中文显示的数字字符串
 */
const numberToChinese = (num: number): string => {
    if (num === 0) return '零'

    const CN_DIGITS = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九']
    const CN_UNITS = ['', '十', '百', '千']
    const CN_SECTIONS = ['', '万', '亿']

    // 分段处理：每4位一段
    let sections: number[] = []
    let temp = num
    while (temp > 0) {
        sections.push(temp % 10000)
        temp = Math.floor(temp / 10000)
    }

    let result = ''
    for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i]
        if (section === 0) continue

        // 转换当前段（0~9999）
        let sectionStr = ''
        let zeroNeeded = false
        let hasNonZero = false

        for (let j = 3; j >= 0; j--) {
            const digit = Math.floor(section / Math.pow(10, j)) % 10
            if (digit === 0) {
                if (hasNonZero) {
                    zeroNeeded = true
                }
            } else {
                if (zeroNeeded) {
                    sectionStr += '零'
                    zeroNeeded = false
                }
                sectionStr += CN_DIGITS[digit] + (j > 0 ? CN_UNITS[j] : '')
                hasNonZero = true
            }
        }

        result += sectionStr + CN_SECTIONS[i]
    }

    // 清理多余的“零”
    return result
        .replace(/零+/g, '零')
        .replace(/零$/, '')
        .replace(/^一十/, '十') // “一十” → “十”（如 10 → 十，不是 一十）
}

function integerToMoneyZh(n: number): string {
    // 定义小数部分的单位（角、分）
    let fraction = ['角', '分'];
    // 定义数字0-9对应的中文大写
    let digit = ['零', '壹', '贰', '叁', '肆', '伍', '陆', '柒', '捌', '玖'];
    // 定义整数部分的单位：
    // 第一层为量级单位（元、万、亿），第二层为位单位（拾、佰、仟）
    let unit = [['', '万', '亿'], ['', '拾', '佰', '仟']];
    // 处理负数情况，若为负数则前缀为"负"，否则为空
    let flag = n < 0 ? '负' : '';
    // 将数字转为绝对值，后续统一处理正数
    n = Math.abs(n);
    // 用于存储转换后的字符串结果
    let result_string = '';
    // 处理小数部分（角和分）
    for (let i = 0; i < fraction.length; i++) {
        // 计算当前小数位的数字（角：n*10取整后模10，分：n*100取整后模10）
        // 拼接数字大写与对应单位（如"伍角"），并移除"零角"或"零分"的冗余表达
        result_string += (digit[Math.floor(n * 10 * Math.pow(10, i)) % 10] + fraction[i]).replace(/零./, '');
    }
    // 若小数部分为空（无角分），则用"整"表示
    result_string = result_string || '';
    // 截取整数部分（去除小数部分）
    n = Math.floor(n);
    // 处理整数部分，按"元、万、亿"量级循环
    for (let i = 0; i < unit[0].length && n > 0; i++) {
        // 存储当前量级（如万级）的转换结果
        let p = '';
        // 处理当前量级下的"拾、佰、仟"位
        for (let j = 0; j < unit[1].length && n > 0; j++) {
            // 从个位开始处理，拼接数字大写与位单位（如"壹佰"）
            p = digit[n % 10] + unit[1][j] + p;
            // 移除已处理的个位数字
            n = Math.floor(n / 10);
        }
        // 清理当前量级的冗余零（移除末尾连续的零，空值用"零"填充）
        // 拼接当前量级单位（如"万"）和之前的结果
        result_string = p.replace(/(零.)*零$/, '').replace(/^$/, '零') + unit[0][i] + result_string;
    }
    // 返回最终结果，去除多余的“零”和“整”字
    return flag + result_string.replace(/(零.)*零元/, '').replace(/(零.)+/g, '零').replace(/^整$/, '');
}

function moneyZh(value: number): string {
    const [intStr, decStr] = value.toFixed(2).split('.')

    const intZh = integerToMoneyZh(Number(intStr))

    // 如果没有小数或小数为 00，则不展示小数
    if (!decStr || decStr === '00') {
        return intZh
    }

    // 小数逐位转中文数字，不加单位
    const decZh = decStr
        .split('')
        .map(d => CN_NUM_MONEY[Number(d)])
        .join('')

    return `${intZh}.${decZh}`
}


/**
 * 截断数字到小数点后两位（不四舍五入）
 */
function normalizeNumber(value: number): number {
    if (value >= 0) {
        return Math.trunc(value * 100) / 100
    } else {
        // 负数也向零截断（符合财务常见需求）
        return -Math.trunc(-value * 100) / 100
    }
}
/**
 * 格式化数字, 保留2位小数, 并添加千分位
 */
function formatThousands(value: number): string {
    return value.toLocaleString('en-US', {
        // 小数部分最少显示 0 位
        minimumFractionDigits: 0,
        // 小数部分最多显示 2 位
        maximumFractionDigits: 2,
    })
}

const baseUnitText = {
    '': '',
    '万': '万',
    '亿': '亿',
}

/**
 * 检测数字的基础单位（万 / 亿）
 */
function detectBaseUnit(value: number): keyof typeof baseUnitText {
    if (value < 10_000) return ''
    if (value < 100_000_000) return '万'
    if (value < 1_000_000_000_000) return '亿'
    throw new Error('数字超出支持范围')
}

function formatValue(
    value: number,
    baseUnit: keyof typeof baseUnitText,
) {
    const divisor = baseUnit === '万' ? 1e4 : baseUnit === '亿' ? 1e8 : 1
    // 除以对应的倍数得到的值
    const t1 = value / divisor
    const impreciseValue = normalizeNumber(t1)

    return {
        precise: formatThousands(value),
        imprecise: formatThousands(impreciseValue),
    }
}

interface FormatNumberResult {
    /**
     * 精确的,仅进行千分位,保留2位小数
     */
    precise: string
    /**
     * 不精确的,仅进行千分位,保留2位小数
     */
    imprecise: string
    /**
     * 转为中文显示
     */
    zh: string
    /**
     * 根据值推断出来的单位
     */
    baseUnit: '' | '万' | '亿'
}

export function formatNumberWithUnitAndChinese(
    value: number,
    unit?: string
): FormatNumberResult {
    if (value === null || value === undefined || Number.isNaN(value)) {
        throw new Error('未传入有效数字')
    }

    const normalized = normalizeNumber(value)
    const baseUnit = detectBaseUnit(normalized)

    const { precise, imprecise } = formatValue(normalized, baseUnit)

    let zh = ''

    if (unit === '元') {
        zh = moneyZh(normalized)
    } else {
        // 拆解为整数部分和小数部分，分别转换为中文
        const [int, dec] = normalized.toString().split('.')
        zh = numberToChinese(Number(int))
        if (dec) {
            // 小数点部分，产品期望是不带分或者元这种的。 所以直接拼接中文
            zh += '.' + dec.split('').map(d => CN_NUM_NORMAL[Number(d)]).join('')
        }
    }

    return {
        precise,
        imprecise,
        zh,
        baseUnit,
    }
}